import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type SharepointLoadBuildingProcessingContextValue = {
  isProcessing: boolean;
  beginGraphFetch: () => void;
  endGraphFetch: () => void;
  setSuggestionSyncProcessing: (processing: boolean) => void;
  setAgileLoading: (loading: boolean) => void;
};

const noop = () => {};

const defaultValue: SharepointLoadBuildingProcessingContextValue = {
  isProcessing: false,
  beginGraphFetch: noop,
  endGraphFetch: noop,
  setSuggestionSyncProcessing: noop,
  setAgileLoading: noop,
};

const SharepointLoadBuildingProcessingContext =
  createContext<SharepointLoadBuildingProcessingContextValue>(defaultValue);

export function useSharepointLoadBuildingProcessing(): SharepointLoadBuildingProcessingContextValue {
  return useContext(SharepointLoadBuildingProcessingContext);
}

type SharepointLoadBuildingProcessingProviderProps = {
  children: ReactNode;
};

export function SharepointLoadBuildingProcessingProvider({
  children,
}: SharepointLoadBuildingProcessingProviderProps) {
  const graphFetchCountRef = useRef(0);
  const [graphFetchCount, setGraphFetchCount] = useState(0);
  const [suggestionSyncProcessing, setSuggestionSyncProcessing] =
    useState(false);
  const [agileLoading, setAgileLoadingState] = useState(false);

  const beginGraphFetch = useCallback(() => {
    graphFetchCountRef.current += 1;
    setGraphFetchCount(graphFetchCountRef.current);
  }, []);

  const endGraphFetch = useCallback(() => {
    graphFetchCountRef.current = Math.max(0, graphFetchCountRef.current - 1);
    setGraphFetchCount(graphFetchCountRef.current);
  }, []);

  const setAgileLoading = useCallback((loading: boolean) => {
    setAgileLoadingState(loading);
  }, []);

  const isProcessing =
    graphFetchCount > 0 || suggestionSyncProcessing || agileLoading;

  const value = useMemo(
    (): SharepointLoadBuildingProcessingContextValue => ({
      isProcessing,
      beginGraphFetch,
      endGraphFetch,
      setSuggestionSyncProcessing,
      setAgileLoading,
    }),
    [
      agileLoading,
      beginGraphFetch,
      suggestionSyncProcessing,
      endGraphFetch,
      graphFetchCount,
      isProcessing,
    ]
  );

  return (
    <SharepointLoadBuildingProcessingContext.Provider value={value}>
      {children}
    </SharepointLoadBuildingProcessingContext.Provider>
  );
}
